import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  Switch,
  Chip
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, Edit as EditOutlinedIcon } from '@mui/icons-material';
import apiClient from '../../networking/axiosconfig';
import { useSelector } from 'react-redux';
import AddCategory from './add';

const Categories = () => {
  const { data: config } = useSelector((state) => state.config);
  const [categories, setCategories] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editPriceRangeDialog, setEditPriceRangeDialog] = useState({
    open: false,
    category: null,
    priceRange: null,
    index: -1
  });
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`${config.API_BASE_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = () => {
    setSelectedCategory(null);
    setIsAddDialogOpen(true);
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setIsEditDialogOpen(true);
  };

  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiClient.delete(`${config.API_BASE_URL}/api/categories/${categoryToDelete._id}`);
      fetchCategories();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleSave = () => {
    setIsAddDialogOpen(false);
    setIsEditDialogOpen(false);
    fetchCategories();
  };

  const filteredCategories = categories.filter(category => 
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category._id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Manage Categories</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddCategory}
        >
          Add Category
        </Button>
      </Box>

      <Box mb={3}>
        <TextField
          label="Search Categories"
          variant="outlined"
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name or ID..."
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Icon</TableCell>
              <TableCell>Price Ranges</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No categories found
                </TableCell>
              </TableRow>
            ) : (
              filteredCategories.map((category) => (
                <TableRow key={category._id}>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>
                    {category.icon && (
                      <img 
                        src={`${config.API_BASE_URL}/images/${category.icon}`} 
                        alt={category.name} 
                        style={{ 
                          width: '50px', 
                          height: '50px', 
                          objectFit: 'contain' 
                        }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {category.priceRanges && category.priceRanges.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {category.priceRanges.map((range, index) => (
                          <Chip 
                            key={index}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <span>{`${range.label} (${range.min} - ${range.max})`}</span>
                                <EditOutlinedIcon 
                                  fontSize="small" 
                                  sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditPriceRangeDialog({
                                      open: true,
                                      category,
                                      priceRange: { ...range },
                                      index
                                    });
                                  }}
                                />
                              </Box>
                            }
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">No price ranges</Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <IconButton onClick={() => handleEditCategory(category)} color="primary">
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDeleteClick(category)} 
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <AddCategory
        open={isAddDialogOpen || isEditDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
          setIsEditDialogOpen(false);
        }}
        onSave={handleSave}
        category={selectedCategory}
        isEdit={isEditDialogOpen}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the category "{categoryToDelete?.name}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={editPriceRangeDialog.open} 
        onClose={() => setEditPriceRangeDialog({ ...editPriceRangeDialog, open: false })}
      >
        <DialogTitle>Edit Price Range</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: '400px' }}>
            <TextField
              label="Label"
              value={editPriceRangeDialog.priceRange?.label || ''}
              onChange={(e) => setEditPriceRangeDialog({
                ...editPriceRangeDialog,
                priceRange: {
                  ...editPriceRangeDialog.priceRange,
                  label: e.target.value
                }
              })}
              fullWidth
              margin="normal"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Min Price"
                type="number"
                value={editPriceRangeDialog.priceRange?.min || ''}
                onChange={(e) => setEditPriceRangeDialog({
                  ...editPriceRangeDialog,
                  priceRange: {
                    ...editPriceRangeDialog.priceRange,
                    min: parseFloat(e.target.value) || 0
                  }
                })}
                fullWidth
                margin="normal"
              />
              <TextField
                label="Max Price"
                type="number"
                value={editPriceRangeDialog.priceRange?.max || ''}
                onChange={(e) => setEditPriceRangeDialog({
                  ...editPriceRangeDialog,
                  priceRange: {
                    ...editPriceRangeDialog.priceRange,
                    max: parseFloat(e.target.value) || 0
                  }
                })}
                fullWidth
                margin="normal"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPriceRangeDialog({ ...editPriceRangeDialog, open: false })}>
            Cancel
          </Button>
          <Button 
            onClick={async () => {
              try {
                const { category, priceRange, index } = editPriceRangeDialog;
                const updatedPriceRanges = [...category.priceRanges];
                updatedPriceRanges[index] = priceRange;
                
                await apiClient.put(`${config.API_BASE_URL}/api/categories/${category._id}`, {
                  ...category,
                  priceRanges: updatedPriceRanges
                });
                
                // Update local state
                setCategories(categories.map(cat => 
                  cat._id === category._id 
                    ? { ...cat, priceRanges: updatedPriceRanges }
                    : cat
                ));
                
                setEditPriceRangeDialog({ ...editPriceRangeDialog, open: false });
              } catch (error) {
                console.error('Error updating price range:', error);
                // Handle error (you might want to show a snackbar/alert here)
              }
            }}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Categories;
